import { useState, useEffect, useCallback } from "react";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { DownOutlined, PlusOutlined, Dropdown } from "@/config/antd";
import PageHeader from "@/components/layout/Header/Page_Header";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import TownHallCard from "@/pages/town_hall/components/Town_Hall_Card";
import EmptyState from "@/pages/town_hall/components/Empty_State";
import TownHallTable from "@/pages/town_hall/components/TownHallTable";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTownHallByAny,
  selectTownHalls,
  selectFetchedTownHalls,
  selectTownHallLoading,
  selectTownHallTotal,
  deleteTownHall,
} from "@/store/redux/slices/townhall/townhall_slice";
import { getOrganizationId } from "@/utils/token_functions";
import { getStatus } from "@/helper/get_status";
import Success from "@/components/common/alerts/Success";
import Delete from "@/components/common/alerts/Delete";
import Breadcrumb from "@/components/specific/Breadcrumb";
// import useNetworkStatus from '../../hooks/useNetworkStatus';

// const isElectron = () => {
//   return window && window.process && window.process.type;
// };

const TownHall = () => {
  const [searchValue, setSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [filterValue, setFilterValue] = useState("all"); // date filter: 'all' | 'today' | 'thisWeek' | 'thisMonth'
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMeetings, setSelectedMeetings] = useState([]);
  const pageSize = 10;
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const townHalls = useSelector(selectTownHalls);
  const fetchedTownHallsByOrg = useSelector(selectFetchedTownHalls);
  const totalTownHalls = useSelector(selectTownHallTotal);
  const isLoading = useSelector(selectTownHallLoading);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [deletedTownHallName, setDeletedTownHallName] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  // const isOnline = useNetworkStatus();

  // Use fetched total or fallback to length
  const [totalTownHall, setTotalTownHall] = useState(0);

  useEffect(() => {
    if (fetchedTownHallsByOrg?.length) {
      setTotalTownHall(fetchedTownHallsByOrg.length);
    } else if (townHalls?.length) {
      setTotalTownHall(townHalls.length);
    } else {
      setTotalTownHall(totalTownHalls || 0);
    }
  }, [townHalls, fetchedTownHallsByOrg, totalTownHalls]);

  useEffect(() => {
    const fetchData = async () => {
      const organizationID = getOrganizationId();

      showLoader();
      try {
        await dispatch(
          fetchTownHallByAny({
            key: "organizationID",
            value: organizationID,
          })
        ).unwrap();

        // Optionally keep the original fetch if needed
        // await dispatch(fetchTownHalls()).unwrap();
      } catch (error) {
        console.error("Error fetching town halls:", error);
      } finally {
        hideLoader();
      }
    };

    fetchData();
  }, [dispatch, hideLoader, showLoader]);

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page when refreshing
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all' to show all data
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("all");

    // 5. Fetch fresh data
    const organizationID = getOrganizationId();

    showLoader();
    try {
      // Refresh town halls by organization ID
      await dispatch(
        fetchTownHallByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error refreshing town halls:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, showLoader, hideLoader]);

  // Use a helper to determine which data source to use
  const getTownHallsToUse = useCallback(() => {
    return fetchedTownHallsByOrg?.length > 0
      ? fetchedTownHallsByOrg
      : townHalls;
  }, [fetchedTownHallsByOrg, townHalls]);

  const applyDateFilter = useCallback(
    (list) => {
      // If no date filter selected or 'all' selected, return as-is
      if (!filterValue || filterValue === "all") return list;

      const now = new Date();

      return list.filter((townHall) => {
        const startDateString = townHall?.duration?.startDate;
        if (!startDateString) return true; // if no date, don't exclude the record

        const startDate = new Date(startDateString);

        // Normalize to date-only for comparisons
        const sameDay =
          startDate.getDate() === now.getDate() &&
          startDate.getMonth() === now.getMonth() &&
          startDate.getFullYear() === now.getFullYear();

        if (filterValue === "today") {
          return sameDay;
        }

        if (filterValue === "thisWeek") {
          // Start of current week (Sunday)
          const startOfWeek = new Date(now);
          startOfWeek.setHours(0, 0, 0, 0);
          startOfWeek.setDate(now.getDate() - now.getDay());

          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 7);

          return startDate >= startOfWeek && startDate < endOfWeek;
        }

        if (filterValue === "thisMonth") {
          return (
            startDate.getMonth() === now.getMonth() &&
            startDate.getFullYear() === now.getFullYear()
          );
        }

        return true;
      });
    },
    [filterValue]
  );

  // Transform data for cards (mobile view)
  const getFilteredCardsData = useCallback(() => {
    let filtered = getTownHallsToUse();

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((townHall) =>
        townHall.title?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter (Expired / Upcoming)
    switch (activeFilter) {
      case "expired":
        filtered = filtered.filter(
          (townHall) => getStatus(townHall.duration) === "Expired"
        );
        break;
      case "upcoming":
        filtered = filtered.filter(
          (townHall) => getStatus(townHall.duration) === "Upcoming"
        );
        break;
      default:
        break;
    }

    // Apply date filter (Today / This Week / This Month)
    filtered = applyDateFilter(filtered);

    // Transform for cards
    return filtered.map((townHall) => ({
      _id: townHall._id,
      id: townHall.id,
      title: townHall.title,
      date: `Published on ${new Date(
        townHall?.duration?.startDate
      ).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })}`,
      status: getStatus(townHall.duration),
    }));
  }, [getTownHallsToUse, searchValue, activeFilter, applyDateFilter]);

  // Transform data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    let filtered = getTownHallsToUse();

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((townHall) =>
        townHall.title?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter (Expired / On Going)
    switch (activeFilter) {
      case "expired":
        filtered = filtered.filter(
          (townHall) => getStatus(townHall.duration) === "Expired"
        );
        break;
      case "onGoing":
        filtered = filtered.filter(
          (townHall) => getStatus(townHall.duration) === "On Going"
        );
        break;
      default:
        break;
    }

    // Apply date filter (Today / This Week / This Month)
    filtered = applyDateFilter(filtered);

    return filtered;
  }, [getTownHallsToUse, searchValue, activeFilter, applyDateFilter]);

  const getTownHallCounts = useCallback(() => {
    const townHallsToCount = getTownHallsToUse();
    if (!townHallsToCount || townHallsToCount.length === 0)
      return { total: 0, expired: 0, onGoing: 0 };

    const counts = {
      total: townHallsToCount.length,
      expired: townHallsToCount.filter(
        (th) => getStatus(th.duration) === "Expired"
      ).length,
      onGoing: townHallsToCount.filter(
        (th) => getStatus(th.duration) === "On Going"
      ).length,
    };

    return counts;
  }, [getTownHallsToUse]);

  // Use this in the early return check
  if (isLoading && getTownHallsToUse().length === 0) {
    return null;
  }

  const cardData = getFilteredCardsData();
  const tableData = getFilteredTableData();
  const townHallCounts = getTownHallCounts();

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleFilterChange = (infoOrKey) => {
    // Ant Design Dropdown passes { key }, SearchFilterBar might pass a raw string.
    const key =
      typeof infoOrKey === "string" ? infoOrKey : infoOrKey?.key ?? "today";
    setFilterValue(key);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSetupClick = () => {
    if (selectedMeetings.length > 0) {
      // Show delete confirmation dialog
      setIsDeleteModalVisible(true);
    } else {
      // Navigate to add new townhall page
      navigate("/dashboard/townhall/add");
    }
  };

  // Function to bulk delete townhalls
  const handleBulkDelete = async () => {
    if (selectedMeetings.length === 0) return;

    setIsDeleteModalVisible(false);
    showLoader();
    try {
      // Get the titles of the townhalls being deleted for the success message
      const townhallsToDelete = getTownHallsToUse().filter((townhall) =>
        selectedMeetings.includes(townhall._id)
      );
      const townhallCount = townhallsToDelete.length;

      // If only one townhall, use its title, otherwise use count
      if (townhallCount === 1) {
        setDeletedTownHallName(townhallsToDelete[0].title);
      } else {
        setDeletedTownHallName(`${townhallCount} town halls`);
      }

      // Delete all selected townhalls
      await Promise.all(
        selectedMeetings.map((townhallId) =>
          dispatch(deleteTownHall(townhallId)).unwrap()
        )
      );

      // Refresh data and reset selection
      await handleRefresh();
      setSelectedMeetings([]);
      setIsDeleteModalVisible(false);
      setSuccessModalVisible(true);
    } catch (error) {
      console.error("Bulk delete failed:", error);
    } finally {
      hideLoader();
    }
  };

  const handleSuccessClose = async () => {
    setSuccessModalVisible(false);
  };

  const handleModify = (id) => {
    showLoader();
    navigate(`/dashboard/townhall/edit/${id}`);
  };

  const filterOptions = {
    items: [
      { key: "all", label: "All Time" },
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  // const renderOfflineMessage = () => {
  //   if (!isElectron()) return null;

  //   if (!isOnline && townHalls.length > 0) {
  //     return (
  //       <div className="mb-4 px-4 py-3 bg-blue-50 border-l-4 border-blue-500 text-blue-700">
  //         <p className="text-sm">
  //           You are currently offline. Viewing cached data.
  //         </p>
  //       </div>
  //     );
  //   }
  //   return null;
  // };

  // breadcrumb items
  const items = [{ title: "Townhall", link: "dashboard/events" }];

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Meetings" },
    { key: "expired", label: "Expired" },
    { key: "onGoing", label: "Upcoming" },
  ];

  return (
    <div className="w-full py-6">
      <Breadcrumb items={items} />

      {/* Page Header */}
      <PageHeader title="Townhall" />

      {/* {isElectron() && renderOfflineMessage()} */}

      <div className="flex items-center justify-between my-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1
              className="heading-medium text-lighterGrey"
              data-testid="townhall-count">
              {totalTownHall} Total Meetings
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of townhall event
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Meetings (${townHallCounts.total})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Expired (${townHallCounts.expired})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "expired" ? "primary" : "white"}
              customTextColor={activeFilter === "expired" ? "white" : "primary"}
              onClick={() => setActiveFilter("expired")}
            />
            <Button
              text={`Upcoming (${townHallCounts.onGoing})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "onGoing" ? "primary" : "white"}
              customTextColor={activeFilter === "onGoing" ? "white" : "primary"}
              onClick={() => setActiveFilter("onGoing")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Button
            text={
              selectedMeetings.length != 0
                ? "DELETE SELECTED"
                : "ADD NEW MEETING"
            }
            icon={
              selectedMeetings.length != 0 ? (
                <img src={TrashIcon} />
              ) : (
                <PlusOutlined />
              )
            }
            color={selectedMeetings.length != 0 ? "outline" : "primary"}
            className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8 whitespace-nowrap"
            iconPosition="left"
            customBgColor={selectedMeetings.length != 0 ? "" : "white"}
            customTextColor={selectedMeetings.length != 0 ? "red" : "primary"}
            onClick={handleSetupClick}
          />
          <Dropdown menu={filterOptions} trigger={["click"]}>
          <Button
              text={
                filterOptions.items.find((opt) => opt.key === filterValue)
                  ?.label || "FILTER BY"
              }
            icon={<DownOutlined />}
            iconPosition="right"
            className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
            customBgColor="white"
            customTextColor="black"
          />
          </Dropdown>
        </div>
        <Button
          text="ADD NEW"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] whitespace-nowrap sm:min-w-[100px] lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleSetupClick}
        />
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search..."
        data-testid="townhall-search-filter"
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {/* Conditional Rendering based on data and filters */}
      {getTownHallsToUse().length === 0 && !isLoading ? (
        <div data-testid="townhall-empty-state">
          <EmptyState onSetup={handleSetupClick} />
        </div>
      ) : cardData.length === 0 ? (
        <div data-testid="townhall-empty-state">
          <EmptyState onSetup={handleSetupClick} />
        </div>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden lg:block">
            {tableData.length > 0 ? (
              <TownHallTable
                data={tableData}
                setSelectedMeetings={setSelectedMeetings}
                onDelete={handleBulkDelete}
                onModify={handleModify}
                onTableChange={handleRefresh}
              />
            ) : (
              <div data-testid="townhall-empty-state">
                <EmptyState onSetup={handleSetupClick} />
              </div>
            )}
          </div>

          {/* Mobile card view */}
          <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
            {cardData.map((townHall) => (
              <TownHallCard
                key={townHall._id}
                {...townHall}
                onDelete={handleBulkDelete}
                onModify={handleModify}
              />
            ))}
          </div>

          {cardData.length > 0 && (
            <div className="mt-6">
              <CustomPagination
                current={currentPage}
                total={totalTownHall}
                pageSize={pageSize}
                onChange={handlePageChange}
                className="pb-8"
              />
            </div>
          )}
        </>
      )}

      {/* Success Modal */}
      <Success
        isVisible={successModalVisible}
        onClose={handleSuccessClose}
        header="Town Hall Deleted"
        paragraph={`"${deletedTownHallName}" has been successfully deleted.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />

      {/* Add Delete confirmation modal */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleBulkDelete}
        header="Delete Selected Town Halls?"
        paragraph={`Are you sure you want to delete ${selectedMeetings.length} selected town hall meetings? This action cannot be undone.`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default TownHall;
